import React from "react";
import { Link } from "react-router-dom";
import { COURSE_1, COURSE_2, BUNDLE, isProductLive } from "../config/iGetToCourse";

/**
 * CourseBanner — reusable promo banner.
 *
 * Props:
 *   product → "bundle" (default) | "course-1" | "course-2"
 *   variant → "card" (default) | "strip" | "compact"
 *
 * Bundle is the default because it's the highest-AOV offer.
 */

const BRAND = {
  deep: "#042F2E",
  teal: "#068fa2",
  gold: "#7BC8CB", // OCEAN Light Teal — gold #d4af6a retired 2026-06-28 per BRAND_VOICE_MASTER.md
  cream: "#efeae7",
  mint: "#5EEAD4",
};

const PRODUCT_MAP = {
  "course-1": COURSE_1,
  "course-2": COURSE_2,
  bundle: BUNDLE,
};

export default function CourseBanner({
  product = "bundle",
  variant = "card",
  testId = "course-banner",
}) {
  const p = PRODUCT_MAP[product] || BUNDLE;
  const LIVE = isProductLive(p);
  const isBundle = p.id === "bundle";

  const ctaLabel = LIVE
    ? `Start now — ${p.priceDisplay}`
    : `Be first to know — ${p.priceDisplay}`;

  const stripEyebrow = LIVE
    ? isBundle
      ? `11:11 Bundle · ${p.priceDisplay} · ${p.savingsDisplay || ""}`
      : `New · ${p.priceDisplay}`
    : `Almost Live · ${p.priceDisplay}`;

  const stripBody = isBundle
    ? "Both I GET TO self-guided courses — 38 pages, master alignment pricing"
    : "Start your I GET TO journey — 20-page self-guided course";

  const stripCta = LIVE ? "Grab it →" : "Get on the list →";

  const cardEyebrow = LIVE
    ? isBundle
      ? `11:11 BUNDLE · ${p.priceDisplay} · ${p.savingsDisplay || ""}`
      : `${p.numerology}`
    : isBundle
    ? `Coming soon · ${p.priceDisplay} (${p.savingsDisplay || ""})`
    : `Coming soon · ${p.priceDisplay}`;

  const cardHeadline = isBundle ? (
    <>
      Begin and{" "}
      <span style={{ color: BRAND.mint, fontStyle: "italic" }}>deepen.</span>
    </>
  ) : (
    <>
      Start your{" "}
      <span style={{ color: BRAND.mint, fontStyle: "italic" }}>I GET TO</span>{" "}
      journey tonight.
    </>
  );

  const cardPitch = isBundle
    ? LIVE
      ? "Both courses — 38 pages of self-guided work. Save $6.65 with master-alignment pricing."
      : "Both courses — 38 pages. Drop your email — be first to start when checkout opens."
    : LIVE
    ? "20-page doable, self-guided course. Read it. Do it. Anchor it. Instant download."
    : "20-page doable, self-guided course. Drop your email — be first to start when checkout opens.";

  if (variant === "strip") {
    return (
      <Link
        to={p.landingUrl}
        className="block w-full transition-all hover:opacity-95"
        style={{
          background: `linear-gradient(90deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
          color: BRAND.cream,
        }}
        data-testid={testId}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-center gap-3 text-center flex-wrap">
          <span
            className="font-sans text-[10px] md:text-xs tracking-[0.3em] uppercase font-bold"
            style={{ color: BRAND.gold }}
          >
            {stripEyebrow}
          </span>
          <span className="font-serif italic text-sm md:text-base">{stripBody}</span>
          <span
            className="font-sans text-xs md:text-sm font-semibold"
            style={{ color: BRAND.mint }}
          >
            {stripCta}
          </span>
        </div>
      </Link>
    );
  }

  if (variant === "compact") {
    return (
      <Link
        to={p.landingUrl}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-sans text-sm font-semibold tracking-wide transition-all hover:scale-[1.02]"
        style={{ background: BRAND.mint, color: BRAND.deep }}
        data-testid={testId}
      >
        ✦ {ctaLabel}
        <span>→</span>
      </Link>
    );
  }

  // Default: card
  return (
    <div
      className="rounded-2xl p-7 md:p-9 max-w-2xl mx-auto"
      style={{
        background: `linear-gradient(135deg, ${BRAND.deep} 0%, ${BRAND.teal} 100%)`,
        color: BRAND.cream,
        boxShadow: `0 20px 60px ${BRAND.deep}33`,
      }}
      data-testid={testId}
    >
      <div className="flex flex-col md:flex-row gap-6 items-center">
        <div className="flex-shrink-0">
          {isBundle ? (
            <div className="flex gap-1">
              <img
                src={BUNDLE.coverImage}
                alt="Start Living I GET TO course — first page preview"
                className="w-24 md:w-32 rounded-sm rotate-[-3deg]"
                style={{ boxShadow: "0 10px 25px rgba(0,0,0,0.4)" }}
              />
              <img
                src={BUNDLE.secondaryCover}
                alt="The Deeper Return course — first page preview"
                className="w-24 md:w-32 rounded-sm rotate-[3deg] -ml-4 mt-2"
                style={{ boxShadow: "0 10px 25px rgba(0,0,0,0.4)" }}
              />
            </div>
          ) : (
            <img
              src={p.coverImage}
              alt={`${p.name} cover`}
              className="w-32 md:w-40 rounded-sm"
              style={{ boxShadow: "0 10px 30px rgba(0,0,0,0.4)" }}
            />
          )}
        </div>
        <div className="flex-1 text-center md:text-left">
          <p
            className="font-sans text-[10px] tracking-[0.4em] uppercase font-bold mb-3"
            style={{ color: BRAND.gold }}
          >
            {cardEyebrow}
          </p>
          <h3
            className="font-serif tracking-tight leading-[1.1] mb-3"
            style={{ fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)" }}
          >
            {cardHeadline}
          </h3>
          <p
            className="font-serif italic text-sm md:text-base leading-relaxed mb-5"
            style={{ color: `${BRAND.cream}cc` }}
          >
            {cardPitch}
          </p>
          <Link
            to={p.landingUrl}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02]"
            style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "14px" }}
            data-testid={`${testId}-cta`}
          >
            {ctaLabel}
            <span>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
